import { Component } from "@angular/core";
import { SiteService } from "@services/site.service";
import { SupervisorRequestService } from "@services/supervisorRequest.service";
import { ToastService } from "angular-toastify";

@Component({
	moduleId: module.id,
	selector: "supervisorPerformance",
	styleUrls: ["supervisorPerformance.css"],
	templateUrl: "supervisorPerformance.html"
})
export class SupervisorPerformanceAdminComponent {
	allRequests: any[] = [];
	isLoading = false;
	openRequests: any[] = [];
	period = "7";
	selectedSiteId: number | null = null;
	sites: any[] = [];
	title = "Supervisor Performance";

	constructor(private toastService: ToastService, private supervisorRequestService: SupervisorRequestService, private siteService: SiteService) {
		this.getOperatorName = this.getOperatorName.bind(this);
		this.getSupervisorName = this.getSupervisorName.bind(this);
		this.getSites();
		this.getData();
		this.getOpenRequests();
	}

	avgMins(requests: any[], field: string): string {
		const valid = requests.filter((r: any) => r[field] != null);
		if (valid.length === 0) return "-";
		const total = valid.reduce((sum: number, r: any) => sum + r[field], 0);
		return (total / valid.length * 60).toFixed(1);
	}

	changePeriod(p: string) {
		this.period = p;
	}

	changeSite(e: any) {
		this.selectedSiteId = e.value || null;
		this.getData();
	}

	filterByDays(days: number): any[] {
		const cutoff = new Date();
		cutoff.setDate(cutoff.getDate() - days);
		return this.allRequests.filter((r: any) => new Date(r.requestOpened) >= cutoff);
	}

	get filtered7(): any[] { return this.filterByDays(7); }
	get filtered30(): any[] { return this.filterByDays(30); }

	get filteredRequests(): any[] {
		if (this.period === "7") return this.filtered7;
		if (this.period === "30") return this.filtered30;
		return this.filtered30; // "both" uses 30 for the grid
	}

	getData() {
		this.isLoading = true;
		this.supervisorRequestService.performanceDashboard(this.selectedSiteId || undefined)
			.subscribe(
				(res: any) => { this.allRequests = res.response || []; this.isLoading = false; },
				(err) => { console.log(err); this.isLoading = false; this.toastService.error("Something Went Wrong!"); }
			);
	}

	getOpenRequests() {
		this.supervisorRequestService.allOpen()
			.subscribe(
				(res: any) => { this.openRequests = res.response || []; },
				(err) => console.log(err)
			);
	}

	getOperatorName(row: any): string {
		return row.operator ? `${row.operator.firstName} ${row.operator.lastName}` : "-";
	}

	getSites() {
		this.siteService.getAll()
			.subscribe(
				(res: any) => { this.sites = res.response?.items || res.response || []; },
				(err) => console.log(err)
			);
	}

	getSupervisorName(row: any): string {
		return row.closingSupervisor ? `${row.closingSupervisor.firstName} ${row.closingSupervisor.lastName}` : "-";
	}

	getTimeClass(val: string): string {
		if (val === "-") return "";
		const mins = parseFloat(val);
		if (mins <= 5) return "fast";
		if (mins <= 15) return "ok";
		return "slow";
	}

	get summaryCards(): any {
		const s7 = this.filtered7;
		const s30 = this.filtered30;
		return {
			avgArrival7: this.avgMins(s7, "timeToArrival"),
			avgArrival30: this.avgMins(s30, "timeToArrival"),
			avgClose7: this.avgMins(s7, "timeOpen"),
			avgClose30: this.avgMins(s30, "timeOpen"),
			count7: s7.length,
			count30: s30.length,
			openCount: this.openRequests.length
		};
	}

	get supervisorLeague(): any[] {
		const data = this.filteredRequests;
		const map: { [key: string]: any } = {};
		for (const r of data) {
			const name = r.closingSupervisor ? `${r.closingSupervisor.firstName} ${r.closingSupervisor.lastName}` : "Unknown";
			const id = r.closingSupervisorId || 0;
			if (!map[id]) map[id] = { avgArrival: 0, avgClose: 0, count: 0, name, totalArrival: 0, totalClose: 0, validArrival: 0, validClose: 0 };
			map[id].count++;
			if (r.timeToArrival != null) { map[id].totalArrival += r.timeToArrival; map[id].validArrival++; }
			if (r.timeOpen != null) { map[id].totalClose += r.timeOpen; map[id].validClose++; }
		}
		return Object.values(map).map((s: any) => ({
			avgArrival: s.validArrival > 0 ? (s.totalArrival / s.validArrival * 60).toFixed(1) : "-",
			avgClose: s.validClose > 0 ? (s.totalClose / s.validClose * 60).toFixed(1) : "-",
			count: s.count,
			name: s.name
		})).sort((a: any, b: any) => {
			const aVal = a.avgArrival === "-" ? 9999 : parseFloat(a.avgArrival);
			const bVal = b.avgArrival === "-" ? 9999 : parseFloat(b.avgArrival);
			return aVal - bVal;
		});
	}

	get byReason(): any[] {
		const data = this.filteredRequests;
		const map: { [key: string]: any } = {};
		for (const r of data) {
			const reason = r.supervisorRequestReason?.reason || "Unknown";
			if (!map[reason]) map[reason] = { count: 0, reason };
			map[reason].count++;
		}
		return Object.values(map).sort((a: any, b: any) => b.count - a.count);
	}

	get byMachine(): any[] {
		const data = this.filteredRequests;
		const map: { [key: string]: any } = {};
		for (const r of data) {
			const name = r.machine?.cellNumber || "Unknown";
			const id = r.machineId || 0;
			if (!map[id]) map[id] = { avgArrival: 0, count: 0, name, totalArrival: 0, validArrival: 0 };
			map[id].count++;
			if (r.timeToArrival != null) { map[id].totalArrival += r.timeToArrival; map[id].validArrival++; }
		}
		return Object.values(map).map((m: any) => ({
			avgArrival: m.validArrival > 0 ? (m.totalArrival / m.validArrival * 60).toFixed(1) : "-",
			count: m.count,
			name: m.name
		})).sort((a: any, b: any) => b.count - a.count);
	}

	headerButtonClick(method: any) {
		// @ts-ignore
		if (this[method]) this[method]();
	}
}
